import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  MapPin,
  Clock,
  DollarSign,
  Users,
  Briefcase,
  Star,
  Send,
  Loader2,
  AlertCircle,
  Sparkles,
  Layers,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { careersService } from "@/api/careersService";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/site/SiteLayout";

function Glass({ className, children }) {
  return (
    <div
      className={cn(
        "glass rounded-3xl shadow-[0_18px_50px_rgba(0,0,0,0.4)]",
        "transition-shadow duration-200 hover:shadow-[0_0_26px_rgba(0,194,255,0.3)]",
        className
      )}
    >
      {children}
    </div>
  );
}

export default function Career() {
  const navigate = useNavigate();

  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await careersService.getPublishedJobs();
      setPositions(data || []);
    } catch (err) {
      console.error("[Career.jsx] Error loading published jobs:", err);
      setError("Unable to load open positions. Please try refreshing the page.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, []);

  const benefits = [
    {
      icon: <MapPin className="h-5 w-5" />,
      title: "Remote First",
      description: "Work from anywhere in the world with flexible hours",
    },
    {
      icon: <DollarSign className="h-5 w-5" />,
      title: "Competitive Pay",
      description: "Market-rate salaries with performance bonuses",
    },
    {
      icon: <Star className="h-5 w-5" />,
      title: "Growth Opportunities",
      description: "Clear career paths and learning & development budget",
    },
    {
      icon: <Users className="h-5 w-5" />,
      title: "Amazing Team",
      description: "Work with passionate professionals who care about quality",
    },
  ];

  const handleApply = (position) => {
    if (position?.slug) {
      navigate(`/apply?job=${position.slug}`);
    } else {
      navigate("/apply");
    }
  };

  const handleLearnMore = (position) => {
    if (position?.slug) {
      navigate(`/careers/${position.slug}`);
    }
  };

  return (
    <div className="space-y-20">
      {/* Hero Section */}
      <section className="relative">
        <div className="w-full px-5 pt-10 md:pt-16 xl:px-10 2xl:px-14">
          <Reveal>
            <div className="max-w-2xl ml-0 mr-auto text-left">
              <Badge className="mb-6 bg-primary/10 text-primary border-primary/20">
                Join Our Team
              </Badge>
              <h1 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">
                Build Your Career <span className="text-primary">With Purpose</span>
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-white/70 max-w-2xl">
                Join a team of passionate professionals building the future of digital solutions.
                Work on meaningful projects while growing your skills in a supportive environment.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() =>
                    document.getElementById("open-positions")?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow"
                >
                  View Open Positions
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  onClick={() => navigate("/culture")}
                  variant="secondary"
                  className="rounded-xl bg-white/10 text-white border-white/20 hover:bg-white/15"
                >
                  Our Culture
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="pt-0">
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal delay={200}>
            <div className="text-left max-w-2xl ml-0 mr-auto">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Why Join Shiveda IT Services?
              </h2>
              <p className="mt-4 text-lg text-white/70">
                More than just a job - it's a place to grow and make an impact
              </p>
            </div>
          </Reveal>

          <div className="mt-8 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit, index) => (
              <Reveal key={benefit.title} delay={300 + index * 100}>
                <Glass className="p-6 text-center h-full">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{benefit.description}</p>
                </Glass>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="open-positions">
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal delay={400}>
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Open Positions</h2>
              <p className="mt-4 text-lg text-white/70">
                Find the perfect role to advance your career
              </p>
            </div>
          </Reveal>

          {/* Loading State */}
          {loading && (
            <div className="py-16 flex flex-col items-center justify-center space-y-4">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
              <p className="text-sm text-white/60">Loading current opportunities...</p>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div className="py-12 px-6 text-center max-w-lg mx-auto rounded-2xl border border-amber-500/20 bg-amber-500/5">
              <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <p className="text-white/80 text-sm mb-4">{error}</p>
              <Button
                onClick={fetchPositions}
                variant="outline"
                className="rounded-xl border-white/20 text-white hover:bg-white/10 text-xs"
              >
                Retry
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && positions.length === 0 && (
            <div className="py-16 text-center max-w-xl mx-auto rounded-3xl border border-white/10 bg-white/[0.02] p-8">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-4">
                <Sparkles className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">No Active Openings Right Now</h3>
              <p className="text-sm text-white/65 mb-6 leading-relaxed">
                We don't have an active position matching at this moment, but we are always excited to meet talented engineers and creators.
              </p>
              <Button
                onClick={() => navigate("/apply")}
                className="rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow font-semibold text-xs"
              >
                Submit a General Application
                <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
            </div>
          )}

          {/* Positions List */}
          {!loading && !error && positions.length > 0 && (
            <div className="space-y-6">
              {positions.map((position, index) => {
                const requiredSkills = (position.job_skills || [])
                  .filter((s) => s.is_required)
                  .map((s) => s.technologies?.name)
                  .filter(Boolean);

                return (
                  <Reveal key={position.id || position.title} delay={500 + index * 100}>
                    <Glass
                      className={cn(
                        "p-6 lg:p-8",
                        position.is_featured && "border border-primary/30 shadow-[0_0_30px_rgba(0,194,255,0.15)]"
                      )}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div className="flex-1 space-y-4">
                          <div className="flex flex-wrap items-center gap-2.5">
                            <h3 className="text-xl font-semibold text-white">{position.title}</h3>
                            {position.is_featured && (
                              <Badge className="bg-primary/15 text-primary border-primary/30 flex items-center gap-1 font-semibold text-xs">
                                <Star className="w-3 h-3 fill-primary" />
                                Featured
                              </Badge>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-4 text-xs sm:text-sm text-white/70">
                            <div className="flex items-center gap-1.5">
                              <Briefcase className="h-4 w-4 text-primary" />
                              <span>{position.departments?.name || "Engineering"}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-4 w-4 text-primary" />
                              <span>{position.employment_type}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-4 w-4 text-primary" />
                              <span>
                                {position.location || "Remote"} ({position.workplace_type})
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-white/40">•</span>
                              <span>
                                {position.experience_min}
                                {position.experience_max ? ` - ${position.experience_max}` : "+"} yrs exp
                              </span>
                            </div>
                            {position.salary_visible && position.salary_min && (
                              <div className="flex items-center gap-1 text-green-400 font-medium">
                                <DollarSign className="h-4 w-4" />
                                <span>
                                  {position.salary_currency || "INR"}{" "}
                                  {Number(position.salary_min).toLocaleString()}
                                  {position.salary_max
                                    ? ` - ${Number(position.salary_max).toLocaleString()}`
                                    : "+"}
                                </span>
                              </div>
                            )}
                          </div>

                          <p className="text-white/80 text-sm leading-relaxed">
                            {position.short_description}
                          </p>

                          {/* Requirements & Skills Snippets */}
                          {position.requirements && position.requirements.length > 0 && (
                            <div className="space-y-1.5 pt-1">
                              <div className="text-xs font-semibold text-white/60">
                                Key Requirements:
                              </div>
                              <ul className="text-xs sm:text-sm text-white/70 space-y-1">
                                {position.requirements.slice(0, 4).map((req, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                                    <span>{req}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {requiredSkills.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5 pt-1">
                              <span className="text-[11px] text-white/50 mr-1 flex items-center gap-1">
                                <Layers className="w-3 h-3 text-primary" />
                                Stack:
                              </span>
                              {requiredSkills.slice(0, 6).map((skillName, i) => (
                                <Badge
                                  key={i}
                                  variant="outline"
                                  className="border-white/15 bg-white/[0.02] text-white/75 text-[11px] py-0 px-2"
                                >
                                  {skillName}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0">
                          <Button
                            onClick={() => handleApply(position)}
                            className="rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow font-semibold text-xs h-10 px-5"
                          >
                            Apply Now
                            <Send className="ml-2 h-3.5 w-3.5" />
                          </Button>
                          <Button
                            onClick={() => handleLearnMore(position)}
                            variant="secondary"
                            className="rounded-xl bg-white/5 text-white border-white/10 hover:bg-white/10 text-xs h-10 px-5"
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    </Glass>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Application Process */}
      <section>
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal delay={600}>
            <Glass className="p-8 lg:p-12">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold tracking-tight mb-4">Our Hiring Process</h2>
                <p className="text-lg text-white/70">
                  A transparent and respectful process designed to find the perfect fit
                </p>
              </div>

              <div className="grid gap-8 lg:grid-cols-4">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary text-black font-bold text-lg flex items-center justify-center mx-auto mb-4">
                    1
                  </div>
                  <h3 className="font-semibold mb-2">Application</h3>
                  <p className="text-sm text-white/70">Submit your resume and background profile</p>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/20 text-primary font-bold text-lg flex items-center justify-center mx-auto mb-4">
                    2
                  </div>
                  <h3 className="font-semibold mb-2">Screening</h3>
                  <p className="text-sm text-white/70">Initial call to discuss your aspirations</p>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/20 text-primary font-bold text-lg flex items-center justify-center mx-auto mb-4">
                    3
                  </div>
                  <h3 className="font-semibold mb-2">Interview</h3>
                  <p className="text-sm text-white/70">Technical and cultural fit assessment</p>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/20 text-primary font-bold text-lg flex items-center justify-center mx-auto mb-4">
                    4
                  </div>
                  <h3 className="font-semibold mb-2">Offer</h3>
                  <p className="text-sm text-white/70">Welcome to the team!</p>
                </div>
              </div>
            </Glass>
          </Reveal>
        </div>
      </section>

      {/* CTA Section */}
      <section>
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal delay={700}>
            <Glass className="p-8 lg:p-12 text-center">
              <h2 className="text-3xl font-bold tracking-tight mb-4">
                Don't See the Right Role?
              </h2>
              <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                We're always interested in connecting with talented individuals. Send us your resume
                and let's discuss future opportunities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate("/apply")}
                  className="rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow"
                >
                  Submit General Application
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  onClick={() => navigate("/contact")}
                  variant="secondary"
                  className="rounded-xl bg-white/10 text-white border-white/20 hover:bg-white/15"
                >
                  Contact Us
                </Button>
              </div>
            </Glass>
          </Reveal>
        </div>
      </section>
    </div>
  );
}